import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { QrCard } from '@/components/synapse/QrCard';
import { Select } from '@/components/synapse/Select';
import { useAuth } from '@/hooks/useAuth';
import { LANGUAGES, useLanguageStore, type Language } from '@/stores/languageStore';
import {
  buildRegistrationUrl,
  registrationQrFileBase,
} from '@/features/patients/registrationLink';

interface Props {
  /** Hide the label and show only the icon — used in the cramped mobile header. */
  iconOnly?: boolean;
}

/**
 * Opens the clinic's patient self-registration QR for printing or display.
 *
 * The chosen language is encoded in the link, so a poster printed in Tamil
 * opens the form in Tamil for whoever scans it. A clinic that serves more than
 * one language prints one poster per language; picking here and re-downloading
 * is the whole workflow.
 */
export function RegistrationQrButton({ iconOnly }: Props) {
  const { t } = useTranslation();
  const { activeClinic } = useAuth();
  const uiLanguage = useLanguageStore((s) => s.language);
  const [open, setOpen] = useState(false);
  // Seeded from the staff member's own language, which is the best guess at
  // what they are about to print. Deliberately NOT kept in sync with it
  // afterwards: once they have picked a poster language, changing the app's
  // language must not silently change what the QR encodes.
  const [language, setLanguage] = useState<Language>(uiLanguage);

  if (!activeClinic) return null;

  const url = buildRegistrationUrl(window.location.origin, activeClinic.slug, language);
  const fileBase = registrationQrFileBase(activeClinic.slug, language);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        {...(iconOnly ? { 'aria-label': t('selfRegistrations.qrButton') } : {})}
      >
        <Icon name="qr" size={15} />
        {!iconOnly && t('selfRegistrations.qrButton')}
      </Button>

      {open && (
        <Modal
          title={t('selfRegistrations.qrModalTitle')}
          description={t('selfRegistrations.qrModalDesc')}
          onClose={() => setOpen(false)}
          footer={
            <Button variant="secondary" full onClick={() => setOpen(false)}>
              {t('common.close')}
            </Button>
          }
        >
          <div className="flex flex-col gap-4">
            <Field
              label={t('selfRegistrations.qrLanguage')}
              hint={t('selfRegistrations.qrLanguageHint')}
            >
              {(id) => (
                <Select
                  id={id}
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as Language)}
                >
                  {/* Native names, not translated ones: the label is for the
                      patient who will read the poster, not the staff member
                      choosing it. */}
                  {LANGUAGES.map((lng) => (
                    <option key={lng.code} value={lng.code}>
                      {lng.nativeLabel}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <QrCard
              url={url}
              fileBase={fileBase}
              title={t('selfRegistrations.qrCardTitle')}
              ariaLabel={t('selfRegistrations.qrAriaLabel')}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
